"use client";
import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Link from "@AdbhutByte/utils/components/Link";
import Stack from "@mui/material/Stack";
import { useFormik } from "formik";
import { styled } from "@mui/material/styles";
import * as yup from "yup";
import TextField from "@mui/material/TextField";
import SendIcon from "@mui/icons-material/Send";
import LoadingButton from "@mui/lab/LoadingButton";
import { enqueueSnackbar } from "notistack";
import { policies } from "@AdbhutByte/utils/layouts/Policies";
import NextImage from "next/image";
import {
  SITE_FALLBACK_NAME,
  SITE_FALLBACK_URL,
} from "@AdbhutByte/utils/universalModifiers";

const Image = styled(NextImage)({});

const baseUrl = process.env.NEXT_PUBLIC_URL ?? SITE_FALLBACK_URL;
const baseName = process.env.NEXT_PUBLIC_SITE_NAME ?? SITE_FALLBACK_NAME;

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required"),
});

function Footer() {
  const [sending, setSending] = React.useState(false);
  const contactFormFooter = useFormik({
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setSending(true);
      // alert(JSON.stringify(values, null, 2));
      const abortLongFetch = new AbortController();
      const abortTimeoutId = setTimeout(() => abortLongFetch.abort(), 7000);
      fetch("/api/email/message/", {
        signal: abortLongFetch.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })
        .then((response) => {
          // response.status !== 200
          if (response.ok) {
            clearTimeout(abortTimeoutId);
            return response.json();
          }
          enqueueSnackbar("Network response was not OK!", {
            variant: "error",
          });
        })
        .then((data) => {
          if (data.emailSent) {
            setSending(false);
            enqueueSnackbar("Email sent successfully!", { variant: "success" });
            resetForm();
          } else {
            setSending(false);
            enqueueSnackbar("Error sending email!", { variant: "error" });
          }
        })
        .catch((error) => {
          setSending(false);
          enqueueSnackbar("Error sending email!", { variant: "error" });
        });
    },
  });
  return (
    <Box component="footer">
      <Box component={Paper}>
        <Container maxWidth={false}>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={3}>
              <Typography fontWeight={700}>Resources</Typography>
              <Stack direction="column" spacing={1} pl={2} pt={1}>
                <Link
                  href="/"
                  color="text.disabled"
                  variant="caption"
                  sx={{ pointerEvents: "none", cursor: "default" }}
                  tabIndex={-1}
                >
                  Blog
                </Link>
                <Link
                  href="/"
                  color="text.disabled"
                  variant="caption"
                  sx={{ pointerEvents: "none", cursor: "default" }}
                  tabIndex={-1}
                >
                  Learn with me
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography fontWeight={700}>More</Typography>
              <Stack direction="column" spacing={1} pl={2} pt={1}>
                <Link
                  href="/"
                  color="text.disabled"
                  variant="caption"
                  sx={{ pointerEvents: "none", cursor: "default" }}
                  tabIndex={-1}
                >
                  ArgonEdge
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography fontWeight={700}>Policies</Typography>
              <Stack direction="column" spacing={1} pl={2} pt={1}>
                {policies.map((policy, i) => {
                  return (
                    <Link
                      key={i}
                      href={`/${policy.link}`}
                      color="text.secondary"
                      variant="caption"
                    >
                      {policy.name}
                    </Link>
                  );
                })}
              </Stack>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography fontWeight={700} pb={1}>
                Say Bonjour!
              </Typography>
              <Box
                component="form"
                onSubmit={contactFormFooter.handleSubmit}
                pt={1}
              >
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Name*"
                  type="text"
                  value={contactFormFooter.values.name}
                  onChange={contactFormFooter.handleChange}
                  onBlur={contactFormFooter.handleBlur}
                  error={
                    contactFormFooter.touched.name &&
                    Boolean(contactFormFooter.errors.name)
                  }
                  helperText=" "
                />
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email*"
                  type="email"
                  value={contactFormFooter.values.email}
                  onChange={contactFormFooter.handleChange}
                  onBlur={contactFormFooter.handleBlur}
                  error={
                    contactFormFooter.touched.email &&
                    Boolean(contactFormFooter.errors.email)
                  }
                  helperText=" "
                />
                <TextField
                  fullWidth
                  id="subject"
                  name="subject"
                  label="Subject*"
                  type="text"
                  value={contactFormFooter.values.subject}
                  onChange={contactFormFooter.handleChange}
                  onBlur={contactFormFooter.handleBlur}
                  error={
                    contactFormFooter.touched.subject &&
                    Boolean(contactFormFooter.errors.subject)
                  }
                  helperText=" "
                />
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  id="message"
                  name="message"
                  label="Message*"
                  type="text"
                  value={contactFormFooter.values.message}
                  onChange={contactFormFooter.handleChange}
                  onBlur={contactFormFooter.handleBlur}
                  error={
                    contactFormFooter.touched.message &&
                    Boolean(contactFormFooter.errors.message)
                  }
                  helperText=" "
                />
                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <LoadingButton
                    id="messageSend"
                    name="messageSend"
                    type="submit"
                    loading={sending}
                    variant="contained"
                    loadingPosition="start"
                    startIcon={<SendIcon />}
                  >
                    <span>{sending ? "Sending" : "Send"}</span>
                  </LoadingButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            <Grid
              container
              spacing={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Grid
                item
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Link
                  href="https://www.dmca.com/Protection/Status.aspx?ID=802ed34c-b379-4f16-b976-abb2d0535090"
                  className="dmca-badge"
                  title="DMCA.com Protection Status"
                >
                  <Typography>
                    <Image
                      src="https://images.dmca.com/Badges/dmca-badge-w150-5x1-10.png?ID=802ed34c-b379-4f16-b976-abb2d0535090"
                      alt="DMCA.com Protection Status"
                      width={150}
                      height={30}
                    />
                  </Typography>
                </Link>
              </Grid>
              <Grid
                item
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Link href={baseUrl + "/humans.txt"}>
                  <Image
                    src="/reserved/icons/misc/humanstxt-transparent-color.png"
                    alt="humans.txt"
                    width={88}
                    height={31}
                  />
                </Link>
              </Grid>
            </Grid>
          </Box>
          <Box textAlign="center" pb={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {new Date().getFullYear()} &copy;{" "}
              <Link variant="inherit" color="inherit" href="/">
                {baseName}
              </Link>
              . All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Footer;
